import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import type { CalendarChange } from "../domain/changes";
import type { CalEvent } from "../types";

/** A change is sent only when this window ends; Undo within it sends nothing. */
export const UNDO_WINDOW_MS = 5000;
export const INFO_TOAST_MS = 2600;
export const ERROR_TOAST_MS = 6000;
/** Wait before re-reading the calendar after a send, so Graph has caught up. */
export const REFRESH_AFTER_SEND_MS = 1500;

export interface CalendarToastState {
  readonly kind: "undo" | "info" | "error";
  readonly message: string;
  readonly onUndo?: () => void;
  /** The change sends an invite to someone outside the organisation. */
  readonly warning?: boolean;
}

/** How long each kind of toast stays when nothing is waiting to be sent. */
const TOAST_MS: Readonly<Record<CalendarToastState["kind"], number>> = {
  undo: UNDO_WINDOW_MS,
  info: INFO_TOAST_MS,
  error: ERROR_TOAST_MS,
};

interface Applied {
  readonly change: CalendarChange;
  readonly sending: boolean;
}

function isForbidden(err: unknown): boolean {
  return typeof err === "object" && err !== null && (err as { status?: unknown }).status === 403;
}

/**
 * The send-after-Undo engine. One change is pending at a time: it shows in the
 * pane at once, and is sent when the Undo window ends, when another change
 * starts, or when the Calendar closes (a tab switch). A failed send is undone
 * in the pane and reported in the toast.
 */
export function useChangeQueue(token: string | null, refreshEvents: () => Promise<void>) {
  const { refreshGraphToken } = useAuthState();
  const [applied, setApplied] = useState<readonly Applied[]>([]);
  const [toast, setToast] = useState<CalendarToastState | null>(null);
  const pendingRef = useRef<CalendarChange | null>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const refreshEventsRef = useRef(refreshEvents);
  refreshEventsRef.current = refreshEvents;
  const refreshRef = useRef(refreshGraphToken);
  refreshRef.current = refreshGraphToken;

  const send = useCallback(async (change: CalendarChange) => {
    const current = tokenRef.current;
    setApplied((list) => list.map((a) => (a.change.id === change.id ? { ...a, sending: true } : a)));
    try {
      if (!current) throw new Error("Not signed in");
      await change.send(current, () => refreshRef.current(false));
      logConnectorSuccess("calendar", `change-${change.kind}`);
      await new Promise((resolve) => window.setTimeout(resolve, REFRESH_AFTER_SEND_MS));
      await refreshEventsRef.current();
    } catch (err) {
      logConnectorFailure("calendar", err, `change-${change.kind}`);
      setToast({
        kind: "error",
        message: isForbidden(err)
          ? "AiVA can't make this change with your permissions. Open the meeting in Outlook."
          : change.failMessage,
      });
    } finally {
      // Sent (the refreshed calendar now shows it) or failed (undo it in the pane).
      setApplied((list) => list.filter((a) => a.change.id !== change.id));
    }
  }, []);

  /** Send the pending change now, if there is one. */
  const flush = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    window.clearTimeout(timerRef.current);
    setToast((t) => (t?.kind === "undo" ? null : t));
    void send(pending);
  }, [send]);

  const undo = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    window.clearTimeout(timerRef.current);
    setApplied((list) => list.filter((a) => a.change.id !== pending.id));
    setToast({ kind: "info", message: "Change undone · nothing was sent" });
  }, []);

  /** Show a change now and send it when the Undo window ends. */
  const stage = useCallback(
    (change: CalendarChange) => {
      flush();
      pendingRef.current = change;
      setApplied((list) => [...list, { change, sending: false }]);
      setToast({ kind: "undo", message: change.message, onUndo: undo, ...(change.warning && { warning: true }) });
      timerRef.current = window.setTimeout(flush, UNDO_WINDOW_MS);
    },
    [flush, undo],
  );

  const notify = useCallback((next: CalendarToastState) => setToast(next), []);
  const dismissToast = useCallback(() => setToast(null), []);

  // A staged change's Undo toast closes when the change is sent. Every other
  // toast (info, error, or an Undo for a local-only choice such as Leave It)
  // times out on its own.
  useEffect(() => {
    if (!toast || (toast.kind === "undo" && pendingRef.current)) return;
    const ms = TOAST_MS[toast.kind];
    const id = window.setTimeout(() => setToast(null), ms);
    return () => window.clearTimeout(id);
  }, [toast]);

  // Leaving the Calendar (e.g. switching tab) sends whatever is pending straight away.
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      window.clearTimeout(timerRef.current);
      const current = tokenRef.current;
      if (!pending || !current) return;
      pendingRef.current = null;
      pending
        .send(current, () => refreshRef.current(false))
        .then(() => logConnectorSuccess("calendar", `change-${pending.kind}`))
        .catch((err: unknown) => logConnectorFailure("calendar", err, `change-${pending.kind}`));
    },
    [],
  );

  /** The calendar as it will be once the pending and in-flight changes land. */
  const applyTo = useCallback(
    (events: readonly CalEvent[]) => applied.reduce<readonly CalEvent[]>((list, a) => a.change.apply(list), events),
    [applied],
  );

  /** Whether a staged change still shows: waiting or being sent, not undone and not failed. */
  const isApplied = useCallback((changeId: number) => applied.some((a) => a.change.id === changeId), [applied]);

  return { stage, flush, undo, notify, dismissToast, toast, applyTo, isApplied, hasPending: applied.length > 0 };
}
